import {
  internalGetSubscriptionsStatusOptions,
  internalGetSubscriptionsStatusQueryKey,
  internalUpdateOrganizationAccountType,
  internalUpdateSubscriptionContractEndsOn,
} from '@civilgrid/rest-client';
import { ClientTable } from '@civilgrid/ui/components';
import { useFeatureFlagEnabled } from '@posthog/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { Notify } from '~/components2/Notify';
import { EditSubscriptionDialog, type SubscriptionEdit } from './EditSubscriptionDialog';
import { getSubscriptionColumns } from './subscriptionColumns';
import type { SubscriptionStatusRow } from './types';

export const SUBSCRIPTION_STATUS_DASHBOARD_FLAG = 'subscription-enable-status-dashboard';

export default function Subscriptions() {
  const enabled = useFeatureFlagEnabled(SUBSCRIPTION_STATUS_DASHBOARD_FLAG);
  if (!enabled) {
    return <p data-testid="subscriptions-disabled">The Subscriptions dashboard isn't enabled for your account.</p>;
  }
  return <SubscriptionsDashboard />;
}

export function SubscriptionsDashboard() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<SubscriptionStatusRow | null>(null);

  const query = useQuery({ ...internalGetSubscriptionsStatusOptions(), refetchOnWindowFocus: false });

  const saveMutation = useMutation({
    mutationFn: async ({ row, edit }: { row: SubscriptionStatusRow; edit: SubscriptionEdit }) => {
      if (edit.contractEndsOn !== row.contractEndsOn) {
        await internalUpdateSubscriptionContractEndsOn({
          path: { organizationId: row.organizationId },
          body: { contractEndsOn: edit.contractEndsOn },
        });
      }
      if (edit.accountType !== row.accountType) {
        await internalUpdateOrganizationAccountType({
          path: { organizationId: row.organizationId },
          body: { accountType: edit.accountType },
        });
      }
    },
    onSuccess: () => {
      setEditing(null);
      Notify.info({ message: 'Subscription updated.' });
      return queryClient.invalidateQueries({ queryKey: internalGetSubscriptionsStatusQueryKey() });
    },
    onError: () => Notify.error({ message: 'Could not update the subscription. Please try again.' }),
  });

  const handleEdit = useCallback((row: SubscriptionStatusRow) => setEditing(row), []);
  const columns = useMemo(() => getSubscriptionColumns(handleEdit), [handleEdit]);

  const inactiveCustomers = (query.data?.rows ?? []).filter(
    (row) => row.status === 'inactive' && row.accountType === 'customer',
  ).length;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 pb-3" data-testid="subscriptions-dashboard" data-fill-height>
      <p className="text-sm" role="status" data-testid="subscriptions-inactive-count">
        {query.data ? `${inactiveCustomers} customer orgs have no active subscription.` : ''}
      </p>
      <ClientTable
        id="admin-subscriptions"
        aria-label="Subscriptions"
        columns={columns}
        data={query.data?.rows ?? []}
        isLoading={query.isLoading}
        enableRowSelection={false}
        enableColumnGrouping={false}
        className="min-h-0 flex-1"
      />
      <EditSubscriptionDialog
        row={editing}
        isSaving={saveMutation.isPending}
        onSave={(row, edit) => saveMutation.mutate({ row, edit })}
        onClose={() => setEditing(null)}
      />
    </div>
  );
}
